import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import type { ComponentType, ReactNode } from 'react';
import { Fragment, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import {
    ContextMenuCheckboxItem,
    ContextMenuItem,
    ContextMenuItemIndicator,
    ContextMenuLabel,
    ContextMenuRadioGroup,
    ContextMenuRadioItem,
    ContextMenuSeparator,
} from '~/ui/core/context-menu/ContextMenu';
import {
    DropdownMenuCheckboxItem,
    DropdownMenuItem,
    DropdownMenuItemIndicator,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconSearch } from '~/ui/core/icons';
import { CheckIcon } from '~/ui/core/icons/CheckIcon';
import { Input } from '~/ui/core/input/Input';

import { AddUserIcon } from '../../legacy/position-management/AddUserIcon';

type Reviewer = {
    readonly id: string;
    readonly firstName: string;
    readonly lastName: string;
};

type MenuCheckboxItemProps = {
    readonly checked?: boolean;
    readonly children: ReactNode;
    readonly onCheckedChange?: (checked: boolean) => void;
};

type MenuRadioGroupProps = {
    readonly children: ReactNode;
    readonly onValueChange?: (value: string) => void;
    readonly value?: string;
};

type MenuComponents = {
    readonly MenuCheckboxItem: ComponentType<MenuCheckboxItemProps>;
    readonly MenuItem: ComponentType<{ readonly children: ReactNode; readonly onSelect?: () => void }>;
    readonly MenuItemIndicator: ComponentType<{ readonly children: ReactNode }>;
    readonly MenuLabel: ComponentType<{ readonly children: ReactNode }>;
    readonly MenuRadioGroup: ComponentType<MenuRadioGroupProps>;
    readonly MenuRadioItem: ComponentType<{ readonly children: ReactNode; readonly value: string }>;
    readonly MenuSeparator: ComponentType<Record<string, never>>;
};

type ReviewerMenuItemsProps = {
    readonly currentStatus?: 'approved' | 'assigned';
    readonly onInvite?: () => void;
    readonly onSearch: (searchTerm: string) => void;
    readonly onSelectReviewer: (reviewer: Reviewer) => void;
    readonly onStatusChange?: (status: 'approved' | 'assigned') => void;
    readonly onUnassignReviewer: () => void;
    readonly reviewers: readonly Reviewer[];
    readonly selectedReviewer?: Reviewer;
};

const StyledInnerContent = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: theme.spacing.xs,
}));

const StyledSearchContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    display: 'flex',
    gap: theme.spacing.s,
}));

const StyledSearchIcon = styled('label')(({ theme }) => ({
    alignItems: 'center',
    color: theme.colors.gray[700],
    height: 16,
    opacity: 0.5,
    width: theme.spacing.m,
}));

const StyledSearchInput = styled(Input)(({ theme }) => ({
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.gray[700],
    flex: 1,
    ...theme.typography.body.S.regular,
    padding: 0,
    paddingLeft: theme.spacing.s,
}));

const StyledIcon = styled.div({
    alignItems: 'center',
    display: 'flex',
    height: 16,
    justifyContent: 'center',
    width: 16,
});

const StyledMenuItemText = styled.div(({ theme }) => ({
    color: theme.colors.gray[900],
    fontSize: theme.typography.body.S.medium.fontSize,
    fontWeight: theme.typography.body.S.medium.fontWeight,
    letterSpacing: theme.typography.body.S.regular.letterSpacing,
}));

const StatusIcon = styled.div<{ readonly status: 'approved' | 'assigned' }>(({ status, theme }) => ({
    alignItems: 'center',
    backgroundColor: status === 'approved' ? theme.colors.success[600] : theme.colors.gray[400],
    borderRadius: '50%',
    display: 'flex',
    height: theme.spacing.m,
    justifyContent: 'center',
    width: theme.spacing.m,
}));

const StyledPlaceholderIcon = styled(IconSearch)({
    height: 16,
    width: 16,
});

const WithGap = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

const StyledReviewerItemContent = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.s,
}));

const StyledReviewersList = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 240,
    overflowY: 'auto',
    scrollbarWidth: 'none',
});

const createReviewerMenuItems = (components: MenuComponents): ((props: ReviewerMenuItemsProps) => ReactNode) => {
    const { MenuCheckboxItem, MenuItem, MenuItemIndicator, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuSeparator } =
        components;

    const StyledMenuCheckboxItem = styled(MenuCheckboxItem)({
        display: 'flex',
    });

    const ReviewerMenuItems = ({
        currentStatus = 'assigned',
        onInvite,
        onSearch,
        onSelectReviewer,
        onStatusChange,
        onUnassignReviewer,
        reviewers = [],
        selectedReviewer,
    }: ReviewerMenuItemsProps): ReactNode => {
        const { translate } = useContext(ApplicationContext);
        const statusOptions = [
            {
                icon: <StatusIcon status="assigned" />,
                label: translate('common.enums.job-state.draft'),
                value: 'assigned' as const,
            },
            {
                icon: <StatusIcon status="approved" />,
                label: translate('common.enums.job-profile-review-state.approved'),
                value: 'approved' as const,
            },
        ];

        return (
            <StyledInnerContent style={{ minHeight: '100px' }}>
                <StyledSearchContainer>
                    <StyledSearchIcon htmlFor="search">
                        <StyledPlaceholderIcon />
                    </StyledSearchIcon>
                    <StyledSearchInput
                        name="search"
                        placeholder={translate('pages.job-architecture-v2.search-reviewers-placeholder')}
                        type="text"
                        onChange={(e) => onSearch(e.target.value)}
                    />
                </StyledSearchContainer>
                <MenuSeparator />
                {!is.undefined(onInvite) && (
                    <Fragment>
                        <MenuItem onSelect={onInvite}>
                            <WithGap>
                                <AddUserIcon />
                                {translate('pages.invitation-dialog.title')}
                            </WithGap>
                        </MenuItem>
                        <MenuSeparator />
                    </Fragment>
                )}
                {!is.undefined(onStatusChange) && (
                    <Fragment>
                        <MenuRadioGroup value={currentStatus} onValueChange={onStatusChange}>
                            {statusOptions.map((status) => (
                                <MenuRadioItem key={status.value} value={status.value}>
                                    <StyledIcon>{status.icon}</StyledIcon>
                                    <StyledMenuItemText>{status.label}</StyledMenuItemText>
                                    <MenuItemIndicator>
                                        <CheckIcon />
                                    </MenuItemIndicator>
                                </MenuRadioItem>
                            ))}
                        </MenuRadioGroup>
                        <MenuSeparator />
                    </Fragment>
                )}
                <MenuItem onSelect={onUnassignReviewer}>
                    <WithGap>
                        {translate('pages.job-architecture-v2.job-unassigned')}
                        {is.undefined(selectedReviewer) && <CheckIcon />}
                    </WithGap>
                </MenuItem>
                <MenuSeparator />
                <MenuLabel>{translate('pages.job-architecture-v2.suggested')}</MenuLabel>
                <StyledReviewersList>
                    {reviewers.map((reviewer) => (
                        <StyledMenuCheckboxItem
                            key={reviewer.id}
                            checked={selectedReviewer?.id === reviewer.id}
                            onCheckedChange={() => onSelectReviewer(reviewer)}
                        >
                            <StyledReviewerItemContent>
                                {reviewer.firstName} {reviewer.lastName}
                            </StyledReviewerItemContent>
                            <MenuItemIndicator>
                                <CheckIcon />
                            </MenuItemIndicator>
                        </StyledMenuCheckboxItem>
                    ))}
                </StyledReviewersList>
            </StyledInnerContent>
        );
    };

    return ReviewerMenuItems;
};

export const ReviewerDropdownMenuItems = createReviewerMenuItems({
    MenuCheckboxItem: DropdownMenuCheckboxItem,
    MenuItem: DropdownMenuItem,
    MenuItemIndicator: DropdownMenuItemIndicator,
    MenuLabel: DropdownMenuLabel,
    MenuRadioGroup: DropdownMenuRadioGroup,
    MenuRadioItem: DropdownMenuRadioItem,
    MenuSeparator: DropdownMenuSeparator,
});

export const ReviewerContextMenuItems = createReviewerMenuItems({
    MenuCheckboxItem: ContextMenuCheckboxItem,
    MenuItem: ContextMenuItem,
    MenuItemIndicator: ContextMenuItemIndicator,
    MenuLabel: ContextMenuLabel,
    MenuRadioGroup: ContextMenuRadioGroup,
    MenuRadioItem: ContextMenuRadioItem,
    MenuSeparator: ContextMenuSeparator,
});

export const ReviewerContextMenu = ReviewerDropdownMenuItems;
